import React, { useEffect, useState, useCallback, useMemo } from "react";
import VisitService from "../services/VisitService";
import AuthService from "../services/AuthService";
import DoctorService from "../services/DoctorService";
import { useNavigate } from "react-router-dom";
import moment from "moment";

function WaitListPage() {
  const [waitingList, setWaitingList] = useState([]);
  const [doctors, setDoctors] = useState([]); 
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // 🔹 NOVO STANJE: Smer sortiranja ('asc', 'desc' ili 'none')
  const [sortDirection, setSortDirection] = useState("none"); 
  
  const navigate = useNavigate();

  const user = AuthService.getCurrentUser();
  const isAdminOrNurse = user?.roles?.some(role => 
    ["ADMIN", "ROLE_ADMIN", "NURSE", "ROLE_NURSE"].includes(role)
  );

  const doctorStats = useMemo(() => {
    if (!isAdminOrNurse) return [];
    const statsMap = {};
    waitingList.forEach(visit => {
      const docId = visit.doctorDTO?.doctorID;
      const docName = `dr ${visit.doctorDTO?.firstName} ${visit.doctorDTO?.lastName}`;
      if (docId) {
        if (!statsMap[docId]) {
          statsMap[docId] = { name: docName, count: 0 };
        }
        statsMap[docId].count += 1;
      }
    });
    return Object.values(statsMap);
  }, [waitingList, isAdminOrNurse]);

  // 🔹 USEMEMO ZA SORTIRANJE: Automatski sortira listu kada se promeni redosled ili lista stigne sa beka
  const sortedWaitingList = useMemo(() => {
    const listCopy = [...waitingList];
    if (sortDirection === "asc") {
      return listCopy.sort((a, b) => moment(a.visitDate).diff(moment(b.visitDate)));
    }
    if (sortDirection === "desc") {
      return listCopy.sort((a, b) => moment(b.visitDate).diff(moment(a.visitDate)));
    }
    return listCopy; // Ako je 'none', vraća originalni redosled sa backenda
  }, [waitingList, sortDirection]);

  // Funkcija koja menja smer na klik dugmeta (none -> asc -> desc -> none)
  const toggleSort = () => {
    setSortDirection((prev) => {
      if (prev === "none") return "asc";
      if (prev === "asc") return "desc";
      return "none";
    });
  };

  const loadWaitingList = useCallback(async (currentUser) => {
    try {
      const isDoctor = currentUser?.roles?.includes("DOCTOR") || currentUser?.roles?.includes("ROLE_DOCTOR");
      let data = [];
      if (isDoctor && !isAdminOrNurse) {
        data = await VisitService.getWaitingList(currentUser.doctorId);
      } else {
        data = await VisitService.getAllWaiting();
      }
      setWaitingList(data);
    } catch (err) {
      setError("Greška pri učitavanju čekaonice.");
    } finally {
      setLoading(false);
    }
  }, [isAdminOrNurse]);

  useEffect(() => {
    const initialize = async () => {
      const currentUser = AuthService.getCurrentUser();
      if (!currentUser) {
        navigate("/login");
        return;
      }
      await loadWaitingList(currentUser);
      if (isAdminOrNurse) {
        try {
          const docData = await DoctorService.getAll();
          if (Array.isArray(docData)) setDoctors(docData);
        } catch (err) {
          console.error("Neuspešno učitavanje doktora", err);
        }
      }
    };
    initialize();
  }, [navigate, loadWaitingList, isAdminOrNurse]);

  const handleUpdateDoctor = async (visit, newDoctorId) => {
    if (!newDoctorId) return;
    setIsUpdating(true);
    try {
      const updatedVisit = {
        ...visit,
        doctorDTO: { doctorID: Number(newDoctorId) }
      };
      await VisitService.updateVisit(visit.visitID, updatedVisit);
      await loadWaitingList(AuthService.getCurrentUser());
    } catch (err) {
      alert("Greška pri promeni doktora: " + err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateTime = async (visit, timeString) => {
    if (!timeString) return;
    setIsUpdating(true);
    try {
      const [hours, minutes] = timeString.split(":");
      
      const newBirthDate = moment(visit.visitDate)
        .hours(Number(hours))
        .minutes(Number(minutes))
        .seconds(0)
        .format("YYYY-MM-DDTHH:mm:ss"); 

      const updatedVisit = {
        ...visit,
        visitDate: newBirthDate
      };

      await VisitService.updateVisit(visit.visitID, updatedVisit);
      await loadWaitingList(AuthService.getCurrentUser());
    } catch (err) {
      alert("Greška pri promeni vremena: " + err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleQuickRemove = async (visit) => {
    try {
        if (visit.status === "WAITING") {
            await VisitService.startVisit(visit.visitID);
        }
        await VisitService.completeVisit(visit.visitID);
        await loadWaitingList(AuthService.getCurrentUser());
    } catch (err) {
        alert("Greška pri uklanjanju: " + err.message);
    }
  };

  const handleStart = async (visit) => {
    try {
      await VisitService.startVisit(visit.visitID);
      goToPatientCard(visit);
    } catch (err) {
      alert("Greška pri pokretanju posete.");
    }
  };

  const handleComplete = async (visitId) => {
    try {
      await VisitService.completeVisit(visitId);
      loadWaitingList(AuthService.getCurrentUser());
    } catch (err) {
      alert("Greška pri završavanju");
    }
  };

  const treatmentTypeLabels = {
    Auriculotherapy: "Aurikuloterapija",
    AestheticMedicine: "Estetska medicina",
    BodyAcupuncture: "Telesna akupunktura",
  };

  const treatmentTypeColors = {
    Auriculotherapy: "#2e7d32",
    AestheticMedicine: "#8e44ad",
    BodyAcupuncture: "#B8860B",
  };

  const getExamTypeLabel = (visit) => {
    const typeName = visit.visitItemDTOS?.[0]?.treatmentTypeDTO?.typeName;
    return treatmentTypeLabels[typeName] || "/";
  };

  const getExamTypeColor = (visit) => {
    const typeName = visit.visitItemDTOS?.[0]?.treatmentTypeDTO?.typeName;
    return treatmentTypeColors[typeName] || "#6c757d";
  };

  const goToPatientCard = (visit) => {
    const patientId = visit.patientDTO?.patientID;
    const typeName = visit.visitItemDTOS?.[0]?.treatmentTypeDTO?.typeName;
    if (typeName === "AestheticMedicine") {
      navigate(`/patients/${patientId}/estetska-medicina`);
    } else if (typeName === "BodyAcupuncture") {
      navigate(`/patients/${patientId}/acupuncture-cards`);
    } else {
      navigate(`/patient-carton/${patientId}`);
    }
  };

  if (loading) return <div className="container mt-4">Učitavanje...</div>;

  const DoctorControl = ({ visit }) =>
    isAdminOrNurse ? (
      <select
        className="form-select form-select-sm"
        value={visit.doctorDTO?.doctorID || ""}
        onChange={(e) => handleUpdateDoctor(visit, e.target.value)}
        disabled={isUpdating}
      >
        {doctors.map((doc) => (
          <option key={doc.doctorID} value={doc.doctorID}>
            dr {doc.firstName} {doc.lastName}
          </option>
        ))}
      </select>
    ) : (
      <span>dr {visit.doctorDTO?.firstName} {visit.doctorDTO?.lastName}</span>
    );

  const TimeControl = ({ visit }) =>
    isAdminOrNurse ? (
      <div className="d-flex align-items-center gap-1 flex-wrap">
        <select
          className="form-select form-select-sm text-center fw-bold"
          style={{ width: "70px" }}
          value={moment(visit.visitDate).format("HH")}
          onChange={(e) => {
            const currentMinutes = moment(visit.visitDate).format("mm");
            handleUpdateTime(visit, `${e.target.value}:${currentMinutes}`);
          }}
          disabled={isUpdating}
        >
          {Array.from({ length: 24 }, (_, i) => {
            const hour = i < 10 ? `0${i}` : `${i}`;
            return <option key={hour} value={hour}>{hour}</option>;
          })}
        </select>
        <span className="fw-bold">:</span>
        <select
          className="form-select form-select-sm text-center fw-bold"
          style={{ width: "70px" }}
          value={moment(visit.visitDate).format("mm")}
          onChange={(e) => {
            const currentHour = moment(visit.visitDate).format("HH");
            handleUpdateTime(visit, `${currentHour}:${e.target.value}`);
          }}
          disabled={isUpdating}
        >
          {Array.from({ length: 60 }, (_, i) => {
            const minute = i < 10 ? `0${i}` : `${i}`;
            return <option key={minute} value={minute}>{minute}</option>;
          })}
        </select>
      </div>
    ) : (
      <span className="fw-bold">{moment(visit.visitDate).format("HH:mm")}h</span>
    );

  const ActionButtons = ({ visit }) => (
    <>
      {visit.status === "WAITING" && (
        <button className="btn btn-success btn-sm me-2" onClick={() => handleStart(visit)}>
          Započni
        </button>
      )}
      {visit.status === "IN_PROGRESS" && (
        <button className="btn btn-primary btn-sm" onClick={() => handleComplete(visit.visitID)}>
          Završi
        </button>
      )}
    </>
  );

  return (
    <div className="container-fluid mt-4 px-3 px-md-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
        <h2 className="mb-0">Čekaonica {isUpdating && <small className="text-muted">(Ažuriranje...)</small>}</h2>
        <button className="btn btn-sm btn-outline-primary" onClick={() => loadWaitingList(AuthService.getCurrentUser())}>
          Osveži
        </button>
      </div>

      {isAdminOrNurse && doctorStats.length > 0 && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm border-0 bg-light">
              <div className="card-body py-2">
                <span className="me-3 fw-bold text-secondary d-block d-sm-inline mb-2 mb-sm-0">Pacijenata po doktoru:</span>
                {doctorStats.map((stat, idx) => (
                  <span key={idx} className="badge bg-white text-dark border me-2 py-2">
                    {stat.name}: <span className="text-primary fw-bold">{stat.count}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Tabela - vidljiva na širim ekranima */}
      <div className="table-responsive d-none d-md-block">
        <table className="table table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th>Pacijent</th>
              <th>Vrsta pregleda</th>
              <th>Doktor (Zadužen)</th>
              <th style={{ minWidth: "180px" }}>
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <span>Vreme prijema</span>
                  <button
                    className={`btn btn-xs ${sortDirection !== 'none' ? 'btn-primary' : 'btn-outline-secondary'} py-0 px-2`}
                    onClick={toggleSort}
                    title="Sortiraj po vremenu"
                    style={{ fontSize: "0.75rem" }}
                  >
                    Sortiraj {sortDirection === "asc" ? "▲" : sortDirection === "desc" ? "▼" : "↕"}
                  </button>
                </div>
              </th>
              <th>Status</th>
              <th>Akcije</th>
            </tr>
          </thead>
          <tbody>
            {sortedWaitingList.map((visit) => (
              <tr key={visit.visitID}>
                <td style={{ minWidth: "200px" }}>
                  <div className="d-flex align-items-center">
                    <button
                      className="btn btn-link text-danger p-0 me-2"
                      title="Ukloni iz čekaonice"
                      style={{ textDecoration: "none", fontSize: "1.2rem", lineHeight: "1" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm("Ukloni pacijenta?")) {
                          handleQuickRemove(visit);
                        }
                      }}
                    >
                      &times;
                    </button>
                    <span
                      onClick={() => goToPatientCard(visit)}
                      style={{ cursor: "pointer", color: "#0d6efd", fontWeight: "bold" }}
                    >
                      {visit.patientDTO?.name} {visit.patientDTO?.surname}
                    </span>
                  </div>
                </td>
                <td>
                  <span className="badge" style={{ backgroundColor: getExamTypeColor(visit), color: "#fff" }}>{getExamTypeLabel(visit)}</span>
                </td>
                <td style={{ minWidth: "220px" }}>
                  <DoctorControl visit={visit} />
                </td>
                <td style={{ minWidth: "170px" }}>
                  <TimeControl visit={visit} />
                </td>
                <td>
                  <span className={`badge ${visit.status === 'IN_PROGRESS' ? 'bg-primary' : 'bg-warning text-dark'}`}>
                    {visit.status}
                  </span>
                </td>
                <td>
                  <ActionButtons visit={visit} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Kartice - vidljive na uskim ekranima */}
      <div className="d-md-none">
        <div className="d-flex justify-content-end mb-2">
          <button
            className={`btn btn-sm ${sortDirection !== 'none' ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={toggleSort}
          >
            Sortiraj po vremenu {sortDirection === "asc" ? "▲" : sortDirection === "desc" ? "▼" : "↕"}
          </button>
        </div>
        {sortedWaitingList.length === 0 ? (
          <p className="text-muted text-center">Nema pacijenata u čekaonici.</p>
        ) : (
          sortedWaitingList.map((visit) => (
            <div className="card shadow-sm mb-3" key={visit.visitID}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start">
                  <span
                    onClick={() => goToPatientCard(visit)}
                    style={{ cursor: "pointer", color: "#0d6efd", fontWeight: "bold", fontSize: "1.05rem" }}
                  >
                    {visit.patientDTO?.name} {visit.patientDTO?.surname}
                  </span>
                  <button
                    className="btn btn-link text-danger p-0"
                    title="Ukloni iz čekaonice"
                    style={{ textDecoration: "none", fontSize: "1.3rem", lineHeight: "1" }}
                    onClick={() => {
                      if (window.confirm("Ukloni pacijenta?")) {
                        handleQuickRemove(visit);
                      }
                    }}
                  >
                    &times;
                  </button>
                </div>

                <div className="mb-2">
                  <span className="badge me-2" style={{ backgroundColor: getExamTypeColor(visit), color: "#fff" }}>{getExamTypeLabel(visit)}</span>
                  <span className={`badge ${visit.status === 'IN_PROGRESS' ? 'bg-primary' : 'bg-warning text-dark'}`}>
                    {visit.status}
                  </span>
                </div>

                <div className="mb-2">
                  <label className="text-muted small mb-1 d-block">Doktor</label>
                  <DoctorControl visit={visit} />
                </div>

                <div className="mb-3">
                  <label className="text-muted small mb-1 d-block">Vreme prijema</label>
                  <TimeControl visit={visit} />
                </div>

                <div className="d-flex justify-content-end">
                  <ActionButtons visit={visit} />
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default WaitListPage;